import type { TripAccessorialDecisionInput } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { decisionToast } from '../policies/accessorial-labels.policy';
import { decideTripAccessorial, getTripAccessorials, type TripAccessorials } from '../trip-accessorials.api';

export type TripAccessorialsState = { status: 'loading' } | { status: 'loaded'; accessorials: TripAccessorials } | { status: 'error' };

/** Loads a trip's proposals and records a decision on one. A refusal comes back as text for the form; success is a toast and the fresh view. */
export function useTripAccessorials(tripId: string) {
  const toast = useToast();
  const [state, setState] = useState<TripAccessorialsState>({ status: 'loading' });

  const reload = useCallback(() => {
    return getTripAccessorials(tripId)
      .then((accessorials) => setState({ status: 'loaded', accessorials }))
      .catch(() => setState({ status: 'error' }));
  }, [tripId]);

  useEffect(() => {
    let cancelled = false;
    getTripAccessorials(tripId)
      .then((accessorials) => !cancelled && setState({ status: 'loaded', accessorials }))
      .catch(() => !cancelled && setState({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  /** Resolves to the server's explanation when refused, or null once recorded. A 409 also reloads, because the view the closer decided on is out of date. */
  const decide = async (input: TripAccessorialDecisionInput): Promise<string | null> => {
    try {
      const accessorials = await decideTripAccessorial(tripId, input);
      setState({ status: 'loaded', accessorials });
      toast.success(decisionToast(input.proposalKey, input.decision));
      return null;
    } catch (err) {
      void reload();
      return apiErrorMessage(err) ?? 'Could not record the decision. Try again.';
    }
  };

  return { state, decide };
}
